import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { media } from '../../other/breakpoints';
import PulsingDot from './PulsingDot';

const dateFormat = require('dateformat');

const EpgContainer = styled.div`
  position: relative;
  background-color: #212529;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  padding-bottom: 1.75rem;
  margin-bottom: 0.5rem;
  margin-left: 1rem;
  color: white;
  overflow: hidden;
  box-sizing: border-box;

  ${media.mobile} {
    margin-left: 0;
    padding: 0.75rem;
    padding-bottom: 1.75rem;
    overflow-x: hidden;
    overflow-y: hidden;
  }
`;

const EpgContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
`;

const EpgList = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  
  & > div {
    flex-shrink: 0;
  }
  
  ${media.mobile} {
    flex-wrap: nowrap;
    gap: 0.25rem;
    overflow: hidden;
  }
`;

const TimeInfo = styled.div`
  color: #aaa;
  font-size: 0.875rem;
  margin-left: 0.5rem;
  flex-shrink: 0;
  white-space: nowrap;
  
  @media (max-width: 900px) {
    margin-left: 0.25rem;
    font-size: 1.2rem;
  }
`;

const EpgTitle = styled.div`
  padding-left: 1rem !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 29vw;
  font-weight: 500;
  box-sizing: border-box;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 900px) {
    padding-left: 0.5rem !important;
    max-width: none;
    flex: 1;
    min-width: 0;
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

const EpgDescription = styled.div`
  font-weight: 100;
  overflow-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  padding-right: 12px;
  margin-top: 0.5rem;
  color: #ccc;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
  word-break: break-word;

  @media (max-width: 900px) {
    padding-right: 0.5rem;
    padding-left: 0;
    -webkit-line-clamp: 4;
    font-size: 1.15rem;
    line-height: 1.5;
  }
`;

const BarsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 0.65rem;
  border-radius: 0 0 0.5rem 0.5rem;
  overflow: hidden;
  z-index: 1;
`;

const BackgroundBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.12);
  z-index: 1;
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 100%;
  background-color: var(--second-color, #ec1650);
  transition: width 0.5s ease;
  border-radius: 0 0 0 0.5rem;
  z-index: 2;
`;

function EpgRow({ title, description, start, stop, first }) {
  const h24Format = useSelector((state) => state.h24);
  const timer = useSelector((state) => state.timer60);

  const startTime = useMemo(() => {
    if (typeof start === 'number') return start;
    if (typeof start === 'string') {
      const parsed = new Date(start).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }, [start]);

  const stopTime = useMemo(() => {
    if (typeof stop === 'number') return stop;
    if (typeof stop === 'string') {
      const parsed = new Date(stop).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }, [stop]);

  const currentTime = useMemo(() => {
    if (typeof timer === 'number' && timer > 0) return timer;
    return Date.now();
  }, [timer]);

  const isActive =
    startTime && stopTime && startTime <= currentTime && currentTime < stopTime;

  const progressPercentage = useMemo(() => {
    if (!startTime || !stopTime) return 0;
    const totalDuration = stopTime - startTime;
    if (totalDuration <= 0) return 0;
    const elapsedTime = currentTime - startTime;
    return Math.max(0, Math.min(100, (elapsedTime / totalDuration) * 100));
  }, [startTime, stopTime, currentTime]);

  return (
    <EpgContainer>
      <BarsContainer>
        <BackgroundBar />
        <ProgressBar
          style={{
            width: `${progressPercentage}%`,
            opacity: progressPercentage > 0 ? 1 : 0,
            minWidth: progressPercentage > 0 ? '3px' : '0px',
          }}
        />
      </BarsContainer>
      <EpgContent>
        <EpgList className="row">
          <div className="col-auto">
            <PulsingDot isPlaying={isActive} />
          </div>
          <div className="col-auto">
            <TimeInfo>
              {startTime && dateFormat(new Date(startTime), h24Format)} -{' '}
              {stopTime && dateFormat(new Date(stopTime), h24Format)}
            </TimeInfo>
          </div>
          <div className="col-auto">
            <EpgTitle>
              <b>{title}</b>
            </EpgTitle>
          </div>
        </EpgList>
        {first && description && description.trim() && (
          <div style={{ marginTop: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
            <EpgDescription>{description}</EpgDescription>
          </div>
        )}
      </EpgContent>
    </EpgContainer>
  );
}

export default EpgRow;
